import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { CatalogImportPage } from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  EntityListDocsTable,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { BackToComponentLink } from './components/catalog/DocsLinks';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { HomePage } from './components/home';
import { AiChatbotSidebar } from '@internal/plugin-ai-chatbot';
import { UnifiedThemeProvider } from '@backstage/theme';
import { htecTheme } from './theme/htecTheme';
import oneLoopAiLogo from './assets/oneloopai-logo.svg';
import { WrapperSection } from './components/ui/WrapperClass';
import { TechDocsErrorBoundary } from './components/ui/TechDocsErrorBoundary';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { ANNOTATION_VIEW_URL } from '@backstage/catalog-model';
import OpenInNew from '@material-ui/icons/OpenInNew';
import GetApp from '@material-ui/icons/GetApp';
import PlayArrow from '@material-ui/icons/PlayArrow';
import Chip from '@material-ui/core/Chip';
import {
  EntityKindPicker,
  EntityTypePicker,
  EntityTagPicker,
} from '@backstage/plugin-catalog-react';

/**
 * WAF-safe fetch interceptor.
 *
 * Azure Application Gateway WAF (OWASP CRS 3.1) blocks requests that contain
 * URL patterns anywhere — query strings (rule 949110) and request bodies (rules
 * 931100-931130) — interpreting them as SSRF attacks. Backstage legitimately
 * passes URLs everywhere: OAuth origin params, catalog location targets,
 * entity validation locations, etc.
 *
 * Two encoding strategies (reversed by the backend waf-bypass middleware):
 *
 * 1. GET requests with URL-containing query params → converted to POST with
 *    params in the body. Duplicate query keys are preserved as arrays.
 *
 * 2. POST/PUT/PATCH with JSON body containing URL patterns → every string
 *    value that contains "http(s)://" is base64-encoded with a "b64:" prefix.
 *
 * Encoded requests carry X-Encoded-Body + X-Waf-Origin headers so the backend
 * can verify they came from the trusted frontend. Cookies are omitted on auth
 * requests to stay under WAF header size limits.
 */
const _origFetch = window.fetch;

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof Request) return input.url;
  return String(input);
}

/** Merge HeadersInit (object, Headers, or [key,val][] tuple array) with extra headers */
function mergeHeaders(
  existing: HeadersInit | undefined,
  extra: Record<string, string>,
): Record<string, string> {
  let base: Record<string, string> = {};
  if (existing instanceof Headers) {
    base = Object.fromEntries(existing.entries());
  } else if (Array.isArray(existing)) {
    base = Object.fromEntries(existing);
  } else if (existing) {
    base = { ...existing };
  }
  return { ...base, ...extra };
}

/** UTF-8–safe base64 encode (btoa alone fails on non-Latin1 chars in URLs) */
function toBase64(str: string): string {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_match, p1) =>
      String.fromCharCode(parseInt(p1, 16)),
    ),
  );
}

const URL_PATTERN = /https?:\/\//;

/**
 * Recursively base64-encode any string value containing a URL pattern.
 * Returns { value, changed } so we can skip re-serialization when nothing changed.
 */
function encodeUrlValues(obj: unknown): { value: unknown; changed: boolean } {
  if (typeof obj === 'string') {
    if (URL_PATTERN.test(obj)) {
      return { value: `b64:${toBase64(obj)}`, changed: true };
    }
    return { value: obj, changed: false };
  }
  if (Array.isArray(obj)) {
    let anyChanged = false;
    const arr = obj.map(item => {
      const r = encodeUrlValues(item);
      if (r.changed) anyChanged = true;
      return r.value;
    });
    return { value: arr, changed: anyChanged };
  }
  if (obj !== null && typeof obj === 'object') {
    let anyChanged = false;
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
      const r = encodeUrlValues(val);
      if (r.changed) anyChanged = true;
      result[key] = r.value;
    }
    return { value: result, changed: anyChanged };
  }
  return { value: obj, changed: false };
}

/** Check if any query param value contains a URL pattern */
function queryHasUrls(params: URLSearchParams): boolean {
  for (const value of params.values()) {
    if (URL_PATTERN.test(value)) return true;
  }
  return false;
}

/**
 * Serialize URLSearchParams preserving duplicate keys as arrays.
 * e.g. ?scope=a&scope=b → { scope: ["a","b"] }
 */
function searchParamsToObject(
  params: URLSearchParams,
): Record<string, string | string[]> {
  const result: Record<string, string | string[]> = {};
  for (const key of params.keys()) {
    const values = params.getAll(key);
    result[key] = values.length === 1 ? values[0] : values;
  }
  return result;
}

window.fetch = function wafSafeFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = getRequestUrl(input);

  // Only intercept same-origin backend API requests
  if (!url.includes('/api/')) {
    return _origFetch.call(this, input, init);
  }

  const isAuth = url.includes('/api/auth/');
  // Only omit cookies on auth start/handler endpoints where large cookies +
  // URL query params trigger WAF. The refresh endpoint needs cookies to
  // access the session containing the refresh token.
  const isAuthStart =
    isAuth && (url.includes('/start') || url.includes('/handler/'));
  // Origin proof so the backend can verify the request came from us, not an
  // external attacker crafting X-Encoded-Body headers.
  const wafOrigin = window.location.origin;

  // --- GET requests with URL-containing query params → POST with body ---
  if (!init?.method || init.method === 'GET') {
    const parsed = new URL(url, window.location.origin);
    if (queryHasUrls(parsed.searchParams)) {
      const cleanUrl = `${parsed.origin}${parsed.pathname}`;
      const params = searchParamsToObject(parsed.searchParams);
      return _origFetch.call(this, cleanUrl, {
        ...init,
        method: 'POST',
        credentials: isAuthStart ? 'omit' : init?.credentials,
        headers: mergeHeaders(init?.headers, {
          'Content-Type': 'application/json',
          'X-Encoded-Body': 'query-to-post',
          'X-Waf-Origin': wafOrigin,
        }),
        body: JSON.stringify(params),
      });
    }
    if (isAuthStart) {
      return _origFetch.call(this, input, { ...init, credentials: 'omit' });
    }
    return _origFetch.call(this, input, init);
  }

  // --- POST/PUT/PATCH with JSON body containing URLs → base64-encode ---
  if (init?.body && typeof init.body === 'string') {
    try {
      const body = JSON.parse(init.body);
      const { value: encoded, changed } = encodeUrlValues(body);
      if (changed) {
        return _origFetch.call(this, input, {
          ...init,
          credentials: isAuthStart ? 'omit' : init.credentials,
          headers: mergeHeaders(init.headers, {
            'Content-Type': 'application/json',
            'X-Encoded-Body': 'b64-urls',
            'X-Waf-Origin': wafOrigin,
          }),
          body: JSON.stringify(encoded),
        });
      }
    } catch {
      // Not JSON — pass through unchanged
    }
  }

  if (isAuthStart) {
    return _origFetch.call(this, input, { ...init, credentials: 'omit' });
  }
  return _origFetch.call(this, input, init);
};

// TODO: Read-only mode -  Enabled the commented out lines after adding the proper Permissions.
const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      // registerApi: catalogImportPlugin.routes.importPage,
      registerApi: false,
    });
    bind(scaffolderPlugin.externalRoutes, {
      // registerComponent: catalogImportPlugin.routes.importPage,
      registerComponent: false,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'htec-theme',
      title: 'HTEC OneLoopAi Blueprints Theme',
      variant: 'light',
      Provider: ({ children }) => {
        return (
          <UnifiedThemeProvider theme={htecTheme}>
            {children}
          </UnifiedThemeProvider>
        );
      },
    },
  ],
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'microsoft-auth-provider',
            title: 'Microsoft Account',
            message:
              'Please use your company Microsoft account. Only authorized company email addresses are supported.',
            apiRef: microsoftAuthApiRef,
          },
        ]}
        title="Sign In to Blueprints"
        align="center"
        titleComponent={
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <img
              src={oneLoopAiLogo}
              alt="HTEC OneLoopAi Blueprints"
              style={{ height: 48, width: 'auto', marginBottom: 24 }}
            />
            <h2
              style={{
                fontSize: 50,
                color: '#2A5A4F',
                fontWeight: 700,
                margin: 0,
              }}
            >
              Sign In
            </h2>
          </div>
        }
      />
    ),
  },
});

// HTEC Engineering Intelligence Platform

// Custom docs columns: show Name, Kind, Type, and Tags.
// Omitted default column: Owner.
const docsColumns = [
  EntityListDocsTable.columns.createTitleColumn({ hidden: true }),
  EntityListDocsTable.columns.createNameColumn(),
  EntityListDocsTable.columns.createKindColumn(),
  EntityListDocsTable.columns.createTypeColumn(),
  {
    title: 'Tags',
    field: 'entity.metadata.tags',
    cellStyle: { padding: '0px 16px 0px 20px' },
    render: ({ entity }: { entity: { metadata: { tags?: string[] } } }) =>
      entity.metadata.tags?.map((t: string) => (
        <Chip
          key={t}
          label={t}
          size="small"
          variant="outlined"
          style={{ marginBottom: '0px' }}
        />
      )),
  },
];

// Extract Bitbucket workspace/repo from any available annotation
function getBitbucketSlug(entity: {
  metadata: { annotations?: Record<string, string> };
}): string | undefined {
  const ann = entity.metadata.annotations || {};
  // 1. Explicit annotation
  const slug = ann['bitbucket.org/project-slug'];
  if (slug) return slug;
  // 2. Parse from source-location or managed-by-location
  for (const key of [
    'backstage.io/source-location',
    'backstage.io/managed-by-origin-location',
    'backstage.io/managed-by-location',
  ]) {
    const val = ann[key];
    if (val) {
      const match = val.match(/bitbucket\.org\/([^/]+\/[^/]+)/);
      if (match) return match[1];
    }
  }
  return undefined;
}

// Custom catalog columns: show only Name, Description (wider), Type, and Tags.
// Omitted default columns: System, Owner, Lifecycle.
const catalogColumns: Parameters<
  typeof CatalogIndexPage
>[0]['columns'] = entityListContext => [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  {
    ...CatalogTable.columns.createNameColumn({
      defaultKind: entityListContext.filters.kind?.value,
    }),
    width: 'auto',
  },
  { ...CatalogTable.columns.createMetadataDescriptionColumn(), width: '40%' },
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createTagsColumn(),
];

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    {/* Custom CatalogIndexPage:
        - columns: custom column set (Name, Description, Type, Tags) via catalogColumns
        - actions: only "View URL" action, removed Favorite star and Edit ( for haveing all the actions remove the actions from component props)
        - filters: sidebar shows only Kind, Type, and Tags (removed Owner, Lifecycle, Processing Status, Namespace) */}
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={catalogColumns}
          actions={[
            ({ entity }) => {
              const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
              return {
                icon: () => <OpenInNew fontSize="small" />,
                tooltip: 'View',
                disabled: !url,
                onClick: () => url && window.open(url, '_blank'),
              };
            },
            ({ entity }) => {
              const slug = getBitbucketSlug(entity);
              const downloadUrl = slug
                ? `/api/repo-download/${slug}`
                : undefined;
              return {
                icon: () => <GetApp fontSize="small" />,
                tooltip: 'Download',
                disabled: !downloadUrl,
                onClick: () =>
                  downloadUrl && window.open(downloadUrl, '_blank'),
              };
            },
            ({ entity }) => {
              const isTemplate = entity.kind.toLowerCase() === 'template';
              const ns = entity.metadata.namespace || 'default';
              const name = entity.metadata.name;
              return {
                icon: () => <PlayArrow fontSize="small" />,
                tooltip: 'Create from Template',
                disabled: !isTemplate,
                onClick: () =>
                  window.location.assign(`/create/templates/${ns}/${name}`),
              };
            },
          ]}
          filters={
            <>
              <EntityKindPicker />
              <EntityTypePicker />
              <EntityTagPicker />
            </>
          }
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    {/* Custom TechDocsIndexPage:
        - columns: custom column set (Name, Kind, Type, Tags), removed Owner */}
    <Route
      path="/docs"
      element={
        <TechDocsIndexPage columns={docsColumns} ownerPickerMode={undefined} />
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <TechDocsErrorBoundary>
          <WrapperSection className="techdocs-wrapper">
            <TechDocsReaderPage />
          </WrapperSection>
        </TechDocsErrorBoundary>
      }
    >
      <TechDocsAddons>
        <BackToComponentLink />
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={<ScaffolderPage contextMenu={{ editor: false }} />}
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/notifications"
      element={
        <WrapperSection className="notifications-wrapper">
          <NotificationsPage themeId="htec-theme" />
        </WrapperSection>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>
        {routes}
        <AiChatbotSidebar />
      </Root>
    </AppRouter>
  </>,
);
